import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const T = "#2dd4bf";
const CHUNK = 4 * 1024 * 1024;

function DmgCard({ adminKey }) {
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(0);
  const [msg, setMsg] = useState("");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/dmg/info`).then((r) => setInfo(r.data));
  }, []);

  const upload = async () => {
    if (!file || !adminKey) { setMsg("Enter your license key and pick the DMG."); return; }
    setMsg(""); setProg(1);
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await axios.post(
          `${API}/api/admin/dmg/chunk?chunk_index=${i}&is_last=${i === total - 1}`,
          blob, { headers: { "x-admin-key": adminKey, "Content-Type": "application/octet-stream" } });
        setProg(Math.round(((i + 1) / total) * 100));
      }
      setMsg("✓ Uploaded! The Download Free Trial button is now live.");
      const r = await axios.get(`${API}/api/admin/dmg/info`);
      setInfo(r.data);
    } catch (e) {
      setMsg(e.response?.status === 403
        ? "Wrong key — use your permanent license key."
        : "Upload failed — try again.");
      setProg(0);
    }
  };

  return (
    <div className="rounded-2xl border border-neutral-700 p-8"
         style={{ background: "#0e1313" }} data-testid="admin-upload-card">
      <h2 className="text-lg font-bold mb-1">Upload App DMG</h2>
      <p className="text-neutral-400 text-sm mb-4">
        {info?.uploaded
          ? `A DMG is live (${(info.size / 1048576).toFixed(1)} MB). Uploading replaces it.`
          : "No DMG uploaded yet — the trial download button shows 'coming soon'."}
      </p>
      <input type="file" accept=".dmg" data-testid="admin-file-input"
             onChange={(e) => setFile(e.target.files[0])}
             className="w-full text-sm mb-4" />
      {prog > 0 && (
        <div className="w-full h-2 bg-neutral-800 rounded mb-3">
          <div className="h-2 rounded" style={{ width: `${prog}%`, background: T }} />
        </div>
      )}
      <button onClick={upload} data-testid="admin-upload-btn"
              className="w-full py-3 rounded-lg font-semibold text-black"
              style={{ background: T }}>
        {prog > 0 && prog < 100 ? `Uploading ${prog}%` : "Upload DMG"}
      </button>
      {msg && <div className="mt-3 text-sm" data-testid="admin-upload-msg">{msg}</div>}
    </div>
  );
}

function SettingsCard({ adminKey }) {
  const [url, setUrl] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/api/settings/youtube`)
      .then((r) => setUrl(r.data.url)).catch(() => {});
  }, []);
  const save = async () => {
    setMsg("");
    try {
      await axios.post(`${API}/api/admin/settings/youtube`, { url },
        { headers: { "x-admin-key": adminKey } });
      setMsg(url ? "✓ Saved — the video is live on the site now."
                 : "✓ Saved — video removed from the site.");
    } catch (e) {
      setMsg(e.response?.status === 403
        ? "Wrong key — enter your permanent license key above."
        : e.response?.data?.detail || "Could not save — try again.");
    }
  };
  return (
    <div className="rounded-2xl border border-neutral-700 p-8"
         style={{ background: "#0e1313" }} data-testid="admin-settings-card">
      <h2 className="text-lg font-bold mb-1">Demo Video (YouTube)</h2>
      <p className="text-neutral-400 text-sm mb-4">
        Paste your YouTube link — it appears instantly in the "3-Step Process"
        section of the site. Clear the field and save to remove it.
      </p>
      <input value={url} placeholder="https://www.youtube.com/watch?v=…"
             data-testid="admin-youtube-input"
             onChange={(e) => setUrl(e.target.value)}
             className="w-full p-3 rounded bg-black border border-neutral-700 mb-3 text-sm" />
      <button onClick={save} data-testid="admin-youtube-save"
              className="px-6 py-2.5 rounded-lg font-semibold text-black text-sm"
              style={{ background: T }}>Save Video Link</button>
      {msg && <div className="mt-3 text-sm" data-testid="admin-youtube-msg">{msg}</div>}
    </div>
  );
}

function VariantUploader({ adminKey, variant, label, hint, uploaded, size, onDone }) {
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(0);
  const [msg, setMsg] = useState("");
  const upload = async () => {
    if (!file) { setMsg("Pick your video file first (MP4 works best)."); return; }
    setMsg(""); setProg(1);
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await axios.post(
          `${API}/api/admin/hero-video/chunk?chunk_index=${i}&is_last=${i === total - 1}&variant=${variant}`,
          blob, { headers: { "x-admin-key": adminKey, "Content-Type": "application/octet-stream" } });
        setProg(Math.round(((i + 1) / total) * 100));
      }
      setMsg("✓ Live on the site."); setProg(0); onDone();
    } catch (e) {
      setMsg(e.response?.status === 403 ? "Wrong key." : "Upload failed — try again.");
      setProg(0);
    }
  };
  const remove = async () => {
    try {
      await axios.post(`${API}/api/admin/hero-video/remove?variant=${variant}`,
        {}, { headers: { "x-admin-key": adminKey } });
      setMsg("Removed."); onDone();
    } catch (e) { setMsg("Could not remove — try again."); }
  };
  return (
    <div className="rounded-xl p-4" style={{ background: "#0b1010" }}
         data-testid={`hero-uploader-${variant}`}>
      <div className="font-semibold text-sm mb-1">{label}
        {uploaded && <span className="ml-2 text-xs" style={{ color: T }}>
          ● live ({(size / 1048576).toFixed(1)} MB)</span>}
      </div>
      <div className="text-neutral-500 text-xs mb-3">{hint}</div>
      <input type="file" accept="video/mp4,video/quicktime"
             data-testid={`hero-video-input-${variant}`}
             onChange={(e) => setFile(e.target.files[0])}
             className="w-full text-sm mb-3" />
      {prog > 0 && (
        <div className="w-full h-2 bg-neutral-800 rounded mb-3">
          <div className="h-2 rounded" style={{ width: `${prog}%`, background: T }} />
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={upload} data-testid={`hero-video-upload-btn-${variant}`}
                className="flex-1 py-2.5 rounded-lg font-semibold text-black text-sm"
                style={{ background: T }}>
          {prog > 0 ? `Uploading ${prog}%` : "Upload"}
        </button>
        {uploaded && (
          <button onClick={remove} data-testid={`hero-video-remove-btn-${variant}`}
                  className="px-4 rounded-lg border border-neutral-700 text-neutral-400 hover:text-white text-xs">
            Remove</button>
        )}
      </div>
      {msg && <div className="mt-2 text-xs" data-testid={`hero-video-msg-${variant}`}>{msg}</div>}
    </div>
  );
}

function HeroVideoCard({ adminKey }) {
  const [info, setInfo] = useState({});
  const refresh = () => axios.get(`${API}/api/settings/hero-video`)
    .then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return (
    <div className="rounded-2xl border border-neutral-700 p-8"
         style={{ background: "#0e1313" }} data-testid="admin-hero-video-card">
      <h2 className="text-lg font-bold mb-1">Hero Videos</h2>
      <p className="text-neutral-400 text-sm mb-4">
        Play a video where the big screenshot is — muted, looping, autoplay.
        Upload both versions so every visitor gets the right shape.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <VariantUploader adminKey={adminKey} variant="landscape"
          label="Desktop (wide 16:9)" uploaded={info.uploaded} size={info.size || 0}
          hint="Shown on computers and iPads held sideways." onDone={refresh} />
        <VariantUploader adminKey={adminKey} variant="portrait"
          label="Phone (vertical 9:16)" uploaded={info.portrait_uploaded}
          size={info.portrait_size || 0}
          hint="Shown on iPhones and iPads held upright." onDone={refresh} />
      </div>
    </div>
  );
}

function SalesCard({ adminKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/sales`,
        { headers: { "x-admin-key": adminKey } });
      setData(data);
    } catch (e) { setErr("Could not load sales."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="rounded-2xl border border-neutral-700 p-8"
         style={{ background: "#0e1313" }} data-testid="admin-sales-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Sales</h2>
        <button onClick={load} data-testid="sales-refresh-btn"
          className="text-xs px-3 py-1.5 rounded-lg border border-neutral-700 text-neutral-400 hover:text-white">
          ↻ Refresh</button>
      </div>
      {err && <div className="text-orange-400 text-sm">{err}</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="rounded-xl p-4 text-center" style={{ background: "#0b1010" }}>
              <div className="text-2xl font-bold" style={{ color: T }}
                   data-testid="sales-revenue">${data.total_revenue.toFixed(2)}</div>
              <div className="text-neutral-500 text-xs mt-1">Total revenue</div>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: "#0b1010" }}>
              <div className="text-2xl font-bold" data-testid="sales-count">{data.total_sales}</div>
              <div className="text-neutral-500 text-xs mt-1">Licenses sold</div>
            </div>
          </div>
          {data.sales.length === 0 ? (
            <div className="text-neutral-500 text-sm" data-testid="sales-empty">
              No sales yet — they'll appear here the moment someone buys.</div>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="sales-list">
              {data.sales.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg"
                     style={{ background: "#0b1010" }}>
                  <div className="min-w-0 mr-2">
                    <div className="text-neutral-200 truncate">{s.email}</div>
                    <div className="font-mono text-neutral-500 truncate">{s.license_key}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={s.status === "refunded" ? "text-red-400" : ""}
                         style={s.status === "paid" ? { color: T } : {}}>
                      {s.status === "refunded" ? "refunded" : `$${s.amount.toFixed(2)}`}
                    </div>
                    <div className="text-neutral-600">{s.date.replace("T", " ")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MessageCard({ m, adminKey, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("");

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !m.read) {
      try {
        await axios.post(`${API}/api/support/messages/${m.id}/read`, {},
          { headers: { "x-admin-key": adminKey } });
        onChanged();
      } catch (e) { /* ignore */ }
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/api/support/messages/${m.id}/reply`,
        { reply }, { headers: { "x-admin-key": adminKey } });
      setStatus("sent"); setReply("");
      onChanged();
    } catch (e) {
      setStatus(e.response?.data?.detail || "Could not send — try again.");
    }
  };

  const when = new Date(m.created_at).toLocaleString();
  return (
    <div className="rounded-xl border border-neutral-800 overflow-hidden"
         style={{ background: "#0b1010" }} data-testid={`inbox-message-${m.id}`}>
      <button onClick={toggle} data-testid={`inbox-message-toggle-${m.id}`}
        className="w-full flex items-center justify-between text-left px-5 py-3 hover:bg-neutral-900 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          {!m.read && <span className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: T }} data-testid="unread-dot" />}
          <span className={`truncate text-sm ${m.read ? "text-neutral-400" : "font-semibold"}`}>
            {m.name} <span className="text-neutral-500">· {m.email}</span>
          </span>
        </div>
        <span className="text-neutral-500 text-xs ml-3 shrink-0">{when}</span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="text-sm text-neutral-200 whitespace-pre-wrap border-l-2 pl-3 py-1"
               style={{ borderColor: T }} data-testid="inbox-message-body">{m.message}</div>
          {m.replies?.map((r, i) => (
            <div key={i} className="mt-3 text-sm text-neutral-400 bg-neutral-900 rounded-lg p-3">
              <div className="text-xs mb-1" style={{ color: T }}>
                ↩ You replied · {new Date(r.sent_at).toLocaleString()}</div>
              <div className="whitespace-pre-wrap">{r.text}</div>
            </div>
          ))}
          <textarea rows={3} value={reply} placeholder="Type your reply — it's emailed to the customer…"
            data-testid={`inbox-reply-input-${m.id}`}
            onChange={(e) => setReply(e.target.value)}
            className="w-full p-3 rounded bg-black border border-neutral-700 text-sm mt-4 mb-2" />
          <button onClick={sendReply} disabled={status === "sending"}
                  data-testid={`inbox-reply-send-${m.id}`}
                  className="px-5 py-2 rounded-lg font-semibold text-black text-sm disabled:opacity-60"
                  style={{ background: T }}>
            {status === "sending" ? "Sending…" : "Send Reply by Email"}
          </button>
          {status === "sent" && (
            <span className="ml-3 text-xs" style={{ color: T }} data-testid="reply-sent-msg">
              ✓ Emailed to {m.email}</span>
          )}
          {status && !["sending", "sent"].includes(status) && (
            <span className="ml-3 text-xs text-orange-400" data-testid="reply-error-msg">
              {status}</span>
          )}
        </div>
      )}
    </div>
  );
}

function InboxCard({ adminKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await axios.get(`${API}/api/support/messages`,
        { headers: { "x-admin-key": adminKey } });
      setData(data);
    } catch (e) {
      setErr(e.response?.status === 403
        ? "Wrong key — sign out and enter your permanent license key."
        : "Could not load messages — try again.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="rounded-2xl border border-neutral-700 p-8"
         style={{ background: "#0e1313" }} data-testid="admin-inbox-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold">Support Inbox</h2>
        {data && data.unread > 0 && (
          <span className="text-xs font-bold px-2 py-1 rounded-full text-black"
                style={{ background: T }} data-testid="inbox-unread-badge">
            {data.unread} unread</span>
        )}
      </div>
      <p className="text-neutral-400 text-sm mb-4">
        Messages from the website contact form. Replies are emailed to the customer.
      </p>
      <button onClick={load} data-testid="inbox-load-btn"
              className="px-5 py-2 rounded-lg font-semibold text-sm border text-white hover:bg-neutral-900 mb-4"
              style={{ borderColor: "#333" }}>
        {data ? "↻ Refresh" : "Open Inbox"}
      </button>
      {err && <div className="text-orange-400 text-sm mb-3" data-testid="inbox-error">{err}</div>}
      {data && data.messages.length === 0 && (
        <div className="text-neutral-500 text-sm" data-testid="inbox-empty">
          No messages yet — they'll appear here as customers write in.</div>
      )}
      {data && (
        <div className="space-y-2">
          {data.messages.map((m) => (
            <MessageCard key={m.id} m={m} adminKey={adminKey} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminUpload() {
  const [key, setKey] = useState(() => localStorage.getItem("wbp_admin_key") || "");
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");

  const login = async () => {
    setErr("");
    try {
      await axios.get(`${API}/api/support/messages`,
        { headers: { "x-admin-key": input } });
      localStorage.setItem("wbp_admin_key", input);
      setKey(input);
    } catch (e) {
      setErr("That key isn't right — use your permanent license key.");
    }
  };

  const logout = () => {
    localStorage.removeItem("wbp_admin_key");
    setKey(""); setInput("");
  };

  if (!key) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center px-4"
           style={{ background: "#090c0c" }}>
        <div className="w-full max-w-sm rounded-2xl border border-neutral-700 p-8"
             style={{ background: "#0e1313" }} data-testid="admin-login-card">
          <h1 className="text-xl font-bold mb-1">Owner Admin</h1>
          <p className="text-neutral-400 text-sm mb-5">
            Enter your key once — this browser will remember you.</p>
          <input type="password" placeholder="Your permanent license key"
                 data-testid="admin-key-input" value={input}
                 onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && login()}
                 className="w-full p-3 rounded bg-black border border-neutral-700 text-sm mb-3" />
          <button onClick={login} data-testid="admin-login-btn"
                  className="w-full py-3 rounded-lg font-semibold text-black"
                  style={{ background: T }}>Sign In</button>
          {err && <div className="text-orange-400 text-xs mt-3"
                       data-testid="admin-login-error">{err}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white py-12 px-4"
         style={{ background: "#090c0c" }}>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Owner Admin</h1>
            <p className="text-neutral-400 text-sm">App download, demo video, and customer support — all in one place.</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn"
                  className="text-xs px-3 py-2 rounded-lg border border-neutral-700 text-neutral-400 hover:text-white">
            Sign out</button>
        </div>
        <InboxCard adminKey={key} />
        <SalesCard adminKey={key} />
        <DmgCard adminKey={key} />
        <HeroVideoCard adminKey={key} />
        <SettingsCard adminKey={key} />
      </div>
    </div>
  );
}
